import { Undef } from 'types/UtilityTypes';
import { Quoting, TMS } from 'types/enums/Integrations';

import { SelectedQuoteType } from './types';

export enum QuoteValueType {
  TotalSell = 'TotalSell', // Total sell rate, including line haul + fuel charges
  Linehaul = 'Linehaul', // The baseline cost of transporting goods, excluding fuel charges
}

export enum QuoteTerminatingAction {
  Reply = 'reply',
  Copy = 'copy',
  Input = 'input',
  Submit = 'submit',
}

export enum QuoteConfirmationStatus {
  Default = 'default',
  ImpliedByUserEdit = 'implied_by_user_edit',
  UserConfirmed = 'user_confirmed',
}

// Inline submit-bid status under the button in SubmitBidSection and matching toast variants.
export enum SubmitBidStatusVariant {
  Success = 'success',
  Info = 'info',
  Warning = 'warning',
  Destructive = 'destructive',
}

type LtlPollCadence = {
  getIntervalMs: (attempt: number) => number;
  maxAttempts: number;
};

/**
 * Default LTL quick quote polling cadence used by non-Tai integrations.
 *
 * Polling is capped at ~2 minutes total:
 * - Attempts 0-3: every 15 seconds (first minute)
 * - Attempts 4-7: every 10 seconds (next 40 seconds)
 * - Attempts 8+: every 5 seconds (final 20 seconds)
 *
 * This keeps load lower at the beginning while still accelerating near timeout.
 */
const DEFAULT_LTL_POLL_CADENCE: LtlPollCadence = {
  getIntervalMs: (attempt: number) => {
    if (attempt < 4) {
      return 15000; // first minute
    }

    if (attempt < 8) {
      return 10000; // next 40s
    }

    return 5000; // final 20s
  },
  maxAttempts: 12,
};

/**
 * Tai LTL polling cadence.
 *
 * We poll more aggressively (every 5 seconds) to better match Tai user
 * expectations and reduce perceived wait time, while preserving an overall
 * ~2-minute ceiling by increasing max attempts accordingly.
 */
const TAI_LTL_POLL_CADENCE: LtlPollCadence = {
  getIntervalMs: () => 5000,
  maxAttempts: 24,
};

const LTL_POLL_CADENCE_BY_TMS: Partial<Record<TMS, LtlPollCadence>> = {
  [TMS.ThreeG]: DEFAULT_LTL_POLL_CADENCE,
  [TMS.Turvo]: DEFAULT_LTL_POLL_CADENCE,
  [TMS.Tai]: TAI_LTL_POLL_CADENCE,
};

function getLtlPollCadence(tmsIntegration: Undef<TMS>): LtlPollCadence {
  if (!tmsIntegration) {
    return DEFAULT_LTL_POLL_CADENCE;
  }

  return LTL_POLL_CADENCE_BY_TMS[tmsIntegration] ?? DEFAULT_LTL_POLL_CADENCE;
}

/**
 * Returns the poll interval for a given LTL integration and attempt number.
 *
 * If no integration is provided (or if it is unknown), we fall back to the
 * default cadence to preserve backward compatibility.
 */
export function getLtlQuotePollIntervalMs(
  attempt: number,
  tmsIntegration?: Undef<TMS>
): number {
  return getLtlPollCadence(tmsIntegration).getIntervalMs(attempt);
}

/**
 * Returns max retry attempts before surfacing an LTL quote polling error.
 *
 * This is integration-aware so we can keep a comparable total timeout window
 * while tuning poll frequency per provider.
 */
export function getLtlQuotePollMaxAttempts(
  tmsIntegration?: Undef<TMS>
): number {
  return getLtlPollCadence(tmsIntegration).maxAttempts;
}

/**
 * Sidebar server submit-bid Drumkit async job + `/submit-bid/result` polling
 * When the sidebar uses `POST /integrations/portal/submit-bid` (quick quote
 * from an email thread with submit-bid eligibility, backend returns 202 + `jobId` and the client
 * polls `POST .../submit-bid/result` until the job finishes.
 */

// Wall-clock cap while waiting for an async Drumkit portal-bid job (must stay aligned with sidebar UX copy).
// 3 minutes.
export const SIDEBAR_SERVER_SUBMIT_BID_ASYNC_MAX_WAIT_MS = 180_000;

// Delay between result polls while Drumkit returns HTTP 202 (job pending/processing).
// every 5 seconds.
export const SIDEBAR_SERVER_SUBMIT_BID_RESULT_POLL_INTERVAL_MS = 5000;

/**
 * Milliseconds between `/integrations/portal/submit-bid/result` polls for a
 * given attempt. Today this is a flat interval; `attempt` is reserved so we
 * can add backoff/jitter later without changing call sites.
 */
export function getSidebarServerSubmitBidResultPollIntervalMs(
  attempt: number
): number {
  void attempt;
  return SIDEBAR_SERVER_SUBMIT_BID_RESULT_POLL_INTERVAL_MS;
}

/**
 * How many result polls we perform before treating the job as still running
 * and surfacing the timeout copy. Derived from max wait ÷ interval so the two
 * stay in sync if either constant changes.
 */
export function getSidebarServerSubmitBidResultPollMaxAttempts(): number {
  return Math.max(
    1,
    Math.ceil(
      SIDEBAR_SERVER_SUBMIT_BID_ASYNC_MAX_WAIT_MS /
        SIDEBAR_SERVER_SUBMIT_BID_RESULT_POLL_INTERVAL_MS
    )
  );
}

/**
 * Shown when `/submit-bid/result` polling exhausts {@link getSidebarServerSubmitBidResultPollMaxAttempts}.
 * Not the same as HTTP 202 while a job is still running, this is a client-side timeout.
 */
export const SUBMIT_PORTAL_BID_RESULT_POLL_TIMEOUT_MESSAGE =
  "We couldn't confirm your bid before timing out. Check the portal or try submitting again.";

/**
 * Shown when server submit-bid fails (start or result), except for
 * {@link SUBMIT_PORTAL_BID_RESULT_POLL_TIMEOUT_MESSAGE}. Raw API messages are not
 * surfaced here; failures are still captured from `lib/api/submitPortalBid` for Sentry.
 */
export const SUBMIT_PORTAL_BID_USER_FACING_FAILURE_MESSAGE =
  "We couldn't submit your bid. Try again in a moment, or submit manually on the portal.";

export const BATCH_QUOTE_EDIT_OPTIONS: {
  value: SelectedQuoteType;
  label: string;
  integration: Quoting;
}[] = [
  {
    value: SelectedQuoteType.DAT_RATEVIEW,
    label: 'DAT RateView',
    integration: Quoting.DAT,
  },
  {
    value: SelectedQuoteType.TRUCKSTOP_BOOKED,
    label: 'Truckstop Booked',
    integration: Quoting.TruckStop,
  },
  {
    value: SelectedQuoteType.TRUCKSTOP_POSTED,
    label: 'Truckstop Posted',
    integration: Quoting.TruckStop,
  },
  {
    value: SelectedQuoteType.GS_BUYPOWER,
    label: 'Greenscreens Buy Power',
    integration: Quoting.Greenscreens,
  },
  {
    value: SelectedQuoteType.GS_NETWORK,
    label: 'Greenscreens Network',
    integration: Quoting.Greenscreens,
  },
];
